import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListPeopleResult,
  type ListPermissionSetsResult,
  type PermissionDiff,
  type ListFieldRulesResult,
} from '@whinerp/client';

/** One set, as the list sends it. */
type PermissionSet = ListPermissionSetsResult['sets'][number];
import { Button, ErrorState, Field, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import type { MessageKey } from '@whinerp/contracts/i18n';
import { formatCount } from '@whinerp/contracts/format';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Permissions, on Setup home (F-006).
 *
 * Preset cards first and the custom-set form behind a secondary action,
 * because six presets answer nearly every workspace and an administrator who
 * opens a permission matrix as their first move is about to build something
 * nobody can maintain.
 *
 * The diff is the part that matters. Changing what somebody may do is the
 * easiest way to silently break their day, so the sets are chosen, the server
 * is asked what would change, and the answer is read before anything is
 * saved. It is the server that answers because the union of several sets is
 * the server's rule - a diff the browser worked out for itself would agree
 * right up to the moment the two disagreed, about the union.
 */

const ACTIONS = ['read', 'create', 'update', 'delete', 'export'] as const;

function sameSelection(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const held = new Set(a);
  return b.every((id) => held.has(id));
}

export function PermissionsPanel({
  people,
  objects,
}: {
  people: ListPeopleResult['people'];
  objects: Array<{ key: string; pluralLabel: string }>;
}): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [selection, setSelection] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  /** The set whose grants are being changed, or null when making a new one. */
  const [editingSet, setEditingSet] = useState<PermissionSet | null>(null);
  const [fieldsFor, setFieldsFor] = useState<string | null>(null);

  const sets = useQuery<ListPermissionSetsResult, ApiError>({
    queryKey: ['permission-sets'],
    queryFn: () => api.listPermissionSets({}),
  });

  /*
   * Asked on every change of the checkboxes, and only while somebody is
   * editing. It writes nothing: the operation is declared `read` for exactly
   * that reason, so an offline queue never replays a question.
   */
  const diff = useQuery<PermissionDiff, ApiError>({
    queryKey: ['permission-diff', editing, [...selection].sort().join(',')],
    queryFn: () =>
      api.previewPermissionSetAssignment({
        path: { userId: editing as string },
        body: { permissionSetIds: selection },
      }),
    enabled: editing !== null,
  });

  const save = useMutation({
    mutationFn: (input: { userId: string; permissionSetIds: string[] }) =>
      api.setPersonPermissionSets({
        path: { userId: input.userId },
        body: { permissionSetIds: input.permissionSetIds },
      }),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['permission-sets'] });
    },
  });

  const setStatus = useMutation({
    mutationFn: (input: { userId: string; status: 'active' | 'deactivated' }) =>
      api.setPersonStatus({ path: { userId: input.userId }, body: { status: input.status } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['setup-people'] });
      await queryClient.invalidateQueries({ queryKey: ['permission-sets'] });
    },
  });

  if (sets.isPending) return <Skeleton className="h-block-md w-full" />;
  if (sets.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={sets.error.message}
        onRetry={() => void sets.refetch()}
      />
    );
  }

  const held = new Map(sets.data.held.map((entry) => [entry.userId, entry.permissionSetIds]));
  const byId = new Map(sets.data.sets.map((set) => [set.permissionSetId, set]));

  return (
    <Panel data-region="permissions" id="permissions">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionLabel>{t('permissions.title')}</SectionLabel>
          {/* Secondary, and it stays secondary. */}
          <Button
            intent="secondary"
            size="small"
            icon="Plus"
            onClick={() => setCreating((open) => !open)}
            aria-expanded={creating}
          >
            {t('permissions.create')}
          </Button>
        </div>
        <p className="text-meta text-ink-meta">{t('permissions.body')}</p>

        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {sets.data.sets.map((set) => (
            <li
              key={set.permissionSetId}
              data-permission-set={set.permissionSetId}
              className="flex flex-col gap-1 rounded-panel border border-rule p-3"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-body font-medium text-ink">{set.name}</span>
                <span className="shrink-0 rounded-chip bg-sunken px-1.5 text-meta text-ink-600">
                  {set.isPreset ? t('permissions.preset') : t('permissions.custom')}
                </span>
              </div>
              {/* The summary, not the matrix. A person choosing between sets is
                  asking what somebody will be able to do. */}
              <p data-permission-summary={set.permissionSetId} className="text-meta text-ink-600">
                {set.summary}
              </p>
              <Num data-permission-members={set.permissionSetId} className="text-meta text-ink-meta">
                {set.members === 0
                  ? t('permissions.holders.none')
                  : t('permissions.holders', { values: { count: formatCount(set.members, format) } })}
              </Num>
              {/*
                * Custom sets only. A preset is what a workspace starts from
                * and what every explanation of this product names; the server
                * refuses to change one, so offering the control would be a
                * button that fails after it is pressed.
                */}
              {set.isPreset ? null : (
                <Button
                  intent="ghost"
                  size="small"
                  className="self-start"
                  data-permission-edit={set.permissionSetId}
                  onClick={() => {
                    setEditingSet(set);
                    setCreating(true);
                  }}
                >
                  {t('permissions.editSet')}
                </Button>
              )}
              <Button
                intent="ghost"
                size="small"
                className="self-start"
                data-open-field-rules={set.permissionSetId}
                aria-expanded={fieldsFor === set.permissionSetId}
                onClick={() =>
                  setFieldsFor((open) => (open === set.permissionSetId ? null : set.permissionSetId))
                }
              >
                {t('fls.open')}
              </Button>
            </li>
          ))}
        </ul>

        {creating ? (
          <CustomSetForm
            // Keyed, so opening a different set rebuilds the form rather than
            // keeping the last one's ticks: the state is seeded from `editing`
            // and React would otherwise reuse the instance.
            key={editingSet?.permissionSetId ?? 'new'}
            objects={objects}
            editing={editingSet}
            onDone={() => {
              setCreating(false);
              setEditingSet(null);
            }}
          />
        ) : null}

        {fieldsFor === null ? null : (
          <FieldRuleMatrix
            permissionSetId={fieldsFor}
            setName={byId.get(fieldsFor)?.name ?? ''}
            objects={objects}
            onDone={() => setFieldsFor(null)}
          />
        )}

        <p className="pt-2 text-micro uppercase tracking-label text-ink-label">{t('permissions.who')}</p>
        <ul className="flex flex-col">
          {people.map((person) => {
            const theirs = held.get(person.userId) ?? [];
            const open = editing === person.userId;
            return (
              <li
                key={person.userId}
                data-person={person.userId}
                className="flex flex-col gap-2 border-t border-t-rule py-2 first:border-t-0"
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-body text-ink">{person.name}</span>
                  {person.status === 'deactivated' ? (
                    <span
                      data-person-status={person.userId}
                      className="rounded-chip bg-warning-tint px-1.5 text-meta text-warning-text"
                    >
                      {t('permissions.status.deactivated')}
                    </span>
                  ) : null}
                  {theirs.length === 0 ? (
                    <span className="text-meta text-ink-meta">{t('permissions.none')}</span>
                  ) : (
                    theirs.map((id) => (
                      <span
                        key={id}
                        data-person-set={id}
                        className="rounded-chip bg-sunken px-1.5 text-meta text-ink-600"
                      >
                        {byId.get(id)?.name ?? id}
                      </span>
                    ))
                  )}
                  <span className="flex-1" />
                  <Button
                    intent="ghost"
                    size="small"
                    onClick={() => {
                      setEditing(open ? null : person.userId);
                      setSelection(theirs);
                    }}
                    aria-expanded={open}
                    aria-label={t('permissions.edit', { values: { name: person.name } })}
                  >
                    {t('permissions.change')}
                  </Button>
                  <Button
                    intent={person.status === 'deactivated' ? 'secondary' : 'danger'}
                    size="small"
                    busy={setStatus.isPending && setStatus.variables?.userId === person.userId}
                    onClick={() =>
                      setStatus.mutate({
                        userId: person.userId,
                        status: person.status === 'deactivated' ? 'active' : 'deactivated',
                      })
                    }
                  >
                    {person.status === 'deactivated'
                      ? t('permissions.reactivate')
                      : t('permissions.deactivate')}
                  </Button>
                </div>

                {open ? (
                  <div data-permission-editor={person.userId} className="flex flex-col gap-2 ps-2">
                    <ul className="flex flex-col gap-1">
                      {sets.data.sets.map((set) => (
                        <li key={set.permissionSetId}>
                          <label className="flex items-baseline gap-2 text-body text-ink">
                            <input
                              type="checkbox"
                              data-permission-choice={set.permissionSetId}
                              checked={selection.includes(set.permissionSetId)}
                              onChange={(event) => {
                                // Read now, not inside the updater: React
                                // clears currentTarget once the handler
                                // returns, and the updater runs after it.
                                const chosen = event.currentTarget.checked;
                                setSelection((current) =>
                                  chosen
                                    ? [...current, set.permissionSetId]
                                    : current.filter((id) => id !== set.permissionSetId),
                                );
                              }}
                            />
                            <span>{set.name}</span>
                            <span className="text-meta text-ink-meta">{set.summary}</span>
                          </label>
                        </li>
                      ))}
                    </ul>

                    {/* Read before saving, never after. */}
                    <div data-permission-diff={person.userId} className="flex flex-col gap-1">
                      <p className="text-micro uppercase tracking-label text-ink-label">
                        {t('permissions.diff.title')}
                      </p>
                      {diff.isPending ? (
                        <p className="text-meta text-ink-meta">{t('permissions.diff.checking')}</p>
                      ) : diff.isError ? (
                        <p className="text-meta text-danger-text">{diff.error.message}</p>
                      ) : diff.data.unchanged ? (
                        <p className="text-meta text-ink-meta">{t('permissions.diff.unchanged')}</p>
                      ) : (
                        <>
                          {diff.data.gains.map((change) => (
                            <p
                              key={`gain-${change.what}`}
                              data-permission-gain
                              className="text-meta text-success-text"
                            >
                              {t('permissions.diff.gains')}: {change.what} — {change.detail}
                            </p>
                          ))}
                          {diff.data.losses.map((change) => (
                            <p
                              key={`loss-${change.what}`}
                              data-permission-loss
                              className="text-meta text-danger-text"
                            >
                              {t('permissions.diff.losses')}: {change.what} — {change.detail}
                            </p>
                          ))}
                        </>
                      )}
                    </div>

                    {save.isError ? (
                      <p data-permission-refusal className="text-meta text-danger-text">
                        {errorMessage(save.error)}
                      </p>
                    ) : null}

                    <div className="flex gap-2">
                      <Button
                        intent="primary"
                        size="small"
                        busy={save.isPending}
                        disabled={sameSelection(selection, theirs)}
                        onClick={() =>
                          save.mutate({ userId: person.userId, permissionSetIds: selection })
                        }
                      >
                        {t('permissions.save')}
                      </Button>
                      <Button intent="ghost" size="small" onClick={() => setEditing(null)}>
                        {t('permissions.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </Panel>
  );
}

/**
 * The custom set form, for a new set or an existing one.
 *
 * Object and action, and the powers. The object grid was here from the start
 * and `capabilities: []` was a literal in the request - so every custom set
 * anybody made was powerless, and nothing on the screen said why.
 *
 * The powers come from the server rather than a list written here, and the
 * server sends only the ones it will store: `platform.*` belongs to the people
 * who run this product, and a picker offering it would be a control that fails
 * after somebody has chosen it.
 *
 * Fields are a matrix of their own and belong to field-level security, not
 * here.
 */
function CustomSetForm({
  objects,
  editing,
  onDone,
}: {
  objects: Array<{ key: string; pluralLabel: string }>;
  /** The set being changed, or null when this is a new one. */
  editing: PermissionSet | null;
  onDone: () => void;
}): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [grants, setGrants] = useState<Record<string, string[]>>(() =>
    Object.fromEntries((editing?.objects ?? []).map((grant) => [grant.objectKey, [...grant.actions]])),
  );
  const [powers, setPowers] = useState<string[]>(() => [...(editing?.capabilities ?? [])]);

  const capabilities = useQuery({
    queryKey: ['capabilities'],
    queryFn: () => api.listCapabilities({}),
  });

  const create = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description,
        objects: Object.entries(grants)
          .filter(([, actions]) => actions.length > 0)
          .map(([objectKey, actions]) => ({
            objectKey,
            actions: actions as Array<(typeof ACTIONS)[number]>,
          })),
        capabilities: powers,
      };
      return editing
        ? api.updatePermissionSet({ path: { permissionSetId: editing.permissionSetId }, body })
        : api.createPermissionSet({ body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['permission-sets'] });
      onDone();
    },
  });

  return (
    <div data-permission-create className="flex flex-col gap-3 rounded-panel border border-rule p-3">
      <Field
        label={t('permissions.create')}
        value={name}
        onChange={(event) => setName(event.currentTarget.value)}
        data-permission-name
      />
      <Field
        label={t('permissions.body')}
        value={description}
        onChange={(event) => setDescription(event.currentTarget.value)}
        data-permission-description
      />
      <div data-scroll-container className="overflow-x-auto">
        <table className="w-full text-meta">
          <thead>
            <tr>
              <th scope="col" className="p-1 text-start text-ink-label">
                {' '}
              </th>
              {ACTIONS.map((action) => (
                <th key={action} scope="col" className="p-1 text-start text-ink-label">
                  {action}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {objects.map((object) => (
              <tr key={object.key}>
                <th scope="row" className="p-1 text-start font-regular text-ink">
                  {object.pluralLabel}
                </th>
                {ACTIONS.map((action) => (
                  <td key={action} className="p-1">
                    <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        aria-label={`${object.pluralLabel} ${action}`}
                        checked={(grants[object.key] ?? []).includes(action)}
                        onChange={(event) => {
                          const chosen = event.currentTarget.checked;
                          setGrants((current) => {
                            const held = current[object.key] ?? [];
                            return {
                              ...current,
                              [object.key]: chosen
                                ? [...held, action]
                                : held.filter((entry) => entry !== action),
                            };
                          });
                        }}
                      />
                    </label>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/*
        * The powers, grouped. Forty in one flat column is a list nobody reads
        * to the bottom, and the grouping is derived from the key rather than
        * from a second map to keep in step.
        */}
      {capabilities.isPending ? (
        <Skeleton className="h-block-sm w-full" />
      ) : capabilities.isError ? (
        <p className="text-meta text-danger-text">{capabilities.error.message}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {capabilities.data.groups.map((group) => (
            <fieldset key={group.key} className="flex flex-col gap-1">
              <legend className="text-micro uppercase tracking-label text-ink-label">
                {group.label}
              </legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {group.capabilities.map((capability) => (
                  <label
                    key={capability.key}
                    className="flex items-center gap-1.5 text-meta text-ink-600"
                  >
                    <input
                      type="checkbox"
                      data-capability={capability.key}
                      checked={powers.includes(capability.key)}
                      onChange={(event) => {
                        /*
                         * Read BEFORE the updater, like the object grid above.
                         *
                         * React nulls `currentTarget` once the handler
                         * returns, and a state updater runs after that - so
                         * reading it inside threw on the first tick and took
                         * the panel down to its error boundary. check:controls
                         * proved this control was bound and could not prove
                         * the handler worked; the UI test is what caught it.
                         */
                        const chosen = event.currentTarget.checked;
                        setPowers((current) =>
                          chosen
                            ? [...current, capability.key]
                            : current.filter((key) => key !== capability.key),
                        );
                      }}
                      className="h-icon-inline w-icon-inline rounded-control border-control"
                    />
                    {capability.label}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
      )}

      {create.isError ? (
        <p data-permission-create-refusal className="text-meta text-danger-text">
          {create.error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button
          intent="primary"
          size="small"
          busy={create.isPending}
          disabled={name.trim() === ''}
          onClick={() => create.mutate()}
        >
          {t('permissions.save')}
        </Button>
        <Button intent="ghost" size="small" onClick={onDone}>
          {t('permissions.cancel')}
        </Button>
      </div>
    </div>
  );
}

/**
 * The field matrix: three states, and who it changes things for.
 *
 * Field security is the easiest way to silently break somebody's day. The
 * person setting a rule is thinking about a field; the count beside it is what
 * makes them think about the people, and it is drawn before the rule is saved
 * rather than in a toast afterwards.
 */
function FieldRuleMatrix({
  permissionSetId,
  setName,
  objects,
  onDone,
}: {
  permissionSetId: string;
  setName: string;
  objects: Array<{ key: string; pluralLabel: string }>;
  onDone: () => void;
}): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [objectKey, setObjectKey] = useState(objects[0]?.key ?? 'contact');

  const rules = useQuery<ListFieldRulesResult, ApiError>({
    queryKey: ['field-rules', permissionSetId],
    queryFn: () => api.listFieldRules({ path: { permissionSetId } }),
  });

  /*
   * The object's fields, from the layout operation.
   *
   * It already returns every field an object has without needing a record,
   * which is exactly the question the matrix asks. A second operation would
   * have been a second answer to it.
   */
  const fields = useQuery({
    queryKey: ['object-layout', objectKey],
    queryFn: () => api.getObjectLayout({ path: { objectKey } }),
  });

  const save = useMutation({
    mutationFn: (input: { fieldKey: string; access: 'hidden' | 'read' | 'edit' }) =>
      api.setFieldRule({
        path: { permissionSetId, objectKey, fieldKey: input.fieldKey },
        body: { access: input.access },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['field-rules', permissionSetId] });
    },
  });

  const ACCESS = ['edit', 'read', 'hidden'] as const;
  const current = new Map(
    (rules.data?.rules ?? [])
      .filter((rule) => rule.objectKey === objectKey)
      .map((rule) => [rule.fieldKey, rule.access]),
  );

  return (
    <div data-field-matrix={permissionSetId} className="flex flex-col gap-3 rounded-panel border border-rule p-3">
      <div className="flex flex-wrap items-baseline gap-3">
        <SectionLabel>
          {t('fls.title')} — {setName}
        </SectionLabel>
        {/* The number of people, before the change rather than after it. */}
        <Num data-field-affected className="text-dense text-ink-meta">
          {rules.data === undefined
            ? ''
            : rules.data.peopleAffected === 0
              ? t('fls.affected.none')
              : t('fls.affected', {
                  values: { count: formatCount(rules.data.peopleAffected, format) },
                })}
        </Num>
        <label className="ms-auto flex items-center gap-2 text-dense text-ink-600">
          {t('fls.object')}
          <select
            data-field-matrix-object
            value={objectKey}
            onChange={(event) => setObjectKey(event.currentTarget.value)}
            className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
          >
            {objects.map((object) => (
              <option key={object.key} value={object.key}>
                {object.pluralLabel}
              </option>
            ))}
          </select>
        </label>
        <Button intent="ghost" size="small" onClick={onDone}>
          {t('fls.close')}
        </Button>
      </div>

      {fields.isPending || rules.isPending ? (
        <Skeleton className="h-block-md w-full" />
      ) : (
        <div data-scroll-container className="overflow-x-auto">
          <table className="w-full text-dense">
            <thead>
              <tr>
                <th scope="col" className="p-1 text-start text-micro uppercase tracking-label text-ink-label">
                  {t('fls.field')}
                </th>
                {ACCESS.map((access) => (
                  <th
                    key={access}
                    scope="col"
                    className="p-1 text-start text-micro uppercase tracking-label text-ink-label"
                  >
                    {t(`fls.access.${access}` as MessageKey)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(fields.data?.fields ?? []).map((field) => {
                const held = current.get(field.key) ?? 'edit';
                return (
                  <tr key={field.key} data-field-row={field.key} className="border-t border-t-rule">
                    <th scope="row" className="p-1 text-start font-regular text-ink">
                      {field.label}
                    </th>
                    {ACCESS.map((access) => (
                      <td key={access} className="p-1">
                        <label className="flex items-center gap-1">
                          <input
                            type="radio"
                            name={`${objectKey}-${field.key}`}
                            data-field-access={`${field.key}:${access}`}
                            aria-label={`${field.label} ${t(`fls.access.${access}` as MessageKey)}`}
                            checked={held === access}
                            onChange={() => save.mutate({ fieldKey: field.key, access })}
                          />
                        </label>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {save.isError ? (
        <p data-field-refusal className="text-dense text-danger-text">
          {save.error.message}
        </p>
      ) : null}
    </div>
  );
}
